// SELECT-body identifier walker (Slice 18 + 19 helper for indexBuilder).
//
// Splits out the QueryStructure → ReferenceRecord traversal so the orchestrator
// in `indexBuilder.ts` stays under the 200-line budget. The walker is pure: it
// reads a `QueryStructure` and emits records into the supplied `IdentifierIndex`
// at byte offsets adjusted back to the ORIGINAL body (`byteOffset` accounts
// for the parsed prefix the orchestrator stripped — `CREATE VIEW … AS` for
// views, `CREATE FUNCTION … RETURN [(]` / `AS $tag$` for inline TVFs).
// Records use `listing.objectKind` so refs from views / inline TVFs / future
// kinds land grouped by their source object type.

import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { QualifiedName, SqlRange } from '../../sql-grammar/model';
import type { FromItemTarget, QueryStructure } from '../../sql-resolver/types';

import type {
  IdentifierIndex,
  IdentifierKind,
  ReferenceKind,
  ReferenceRecord,
} from '../types';

import type { PersistedObjectListing } from './objectBodyFetcher';

export function walkSelect(
  qs: QueryStructure,
  listing: PersistedObjectListing,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  if (qs.selectItems) {
    for (const item of qs.selectItems) {
      if (item.resolved && item.resolved.kind === 'column') {
        emitRef(
          listing,
          canonicalizeColumn(item.resolved.column),
          'column',
          item.range,
          'read',
          byteOffset,
          lineIdx,
          index,
          item.resolved.relation.qualified,
        );
      }
    }
  }
  if (qs.fromItems) {
    for (const from of qs.fromItems) walkTarget(from.target, from.range, listing, byteOffset, lineIdx, index);
  }
  if (qs.joins) {
    for (const join of qs.joins) {
      walkTarget(join.right.target, join.right.range, listing, byteOffset, lineIdx, index);
    }
  }
  if (qs.groupBy) {
    for (const ref of qs.groupBy) {
      emitRef(listing, canonicalizeColumn(ref.qualified), 'column', ref.range, 'read', byteOffset, lineIdx, index);
    }
  }
  if (qs.compoundBranches) {
    for (const branch of qs.compoundBranches) walkSelect(branch, listing, byteOffset, lineIdx, index);
  }
}

function walkTarget(
  target: FromItemTarget,
  range: SqlRange,
  listing: PersistedObjectListing,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  if (target.kind === 'relation') {
    // #33: record ONLY the terminal object token (`accounts` in
    // `dbo.accounts a`), not the whole FromItem range — so the rename planner
    // rewrites just the name and preserves the schema qualifier + alias. The
    // whole-item `range` is intentionally ignored for relations.
    //
    // #34: record the FULL qualified identity (schema/db intact) so the
    // schema-aware comparator can distinguish `sales.orders` from
    // `archive.orders`. Broad candidate retrieval keys on the leaf name only
    // (`queryRelationCandidates`), so keeping schema here no longer regresses
    // lookup. `refPartCount` flags 4-part linked-server refs as ambiguous.
    emitRef(
      listing,
      target.qualified,
      'table',
      target.nameRange,
      'read',
      byteOffset,
      lineIdx,
      index,
      undefined,
      target.partCount,
    );
  } else if (target.kind === 'function-call') {
    emitRef(listing, target.routine, 'routine', range, 'call', byteOffset, lineIdx, index);
  } else if (target.kind === 'derived-table') {
    walkSelect(target.subquery, listing, byteOffset, lineIdx, index);
  }
  // 'cte-ref' refs are scoped to the WITH-clause body — buffer-only, do not
  // cross-index. Mirrors `IdentifierKind`'s exclusion of CTE/alias.
}

/** Strip the schema component of a column QualifiedName before recording.
 *
 *  Column refs in view bodies are typically alias-prefixed (`u.email`); the
 *  alias-as-schema is buffer-scoped noise that would split the index across
 *  every alias spelling, so the index keys all `email` refs under one bucket.
 *  Cross-table disambiguation (NAV-1) is preserved separately: every column
 *  record also carries the resolved `hostTable` (the relation the ref bound
 *  to here), and the rename planner filters on it so renaming `users.email`
 *  never rewrites a body that references `orders.email`. */
function canonicalizeColumn(qn: QualifiedName): QualifiedName {
  return {
    name: qn.name,
    raw: qn.name,
    quoted: qn.quoted,
    foldedName: qn.foldedName,
  };
}

function emitRef(
  listing: PersistedObjectListing,
  identifier: QualifiedName,
  identifierKind: IdentifierKind,
  rawRange: SqlRange,
  referenceKind: ReferenceKind,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
  hostTable?: QualifiedName,
  refPartCount?: number,
): void {
  const adjusted = rangeFromOffsets(
    lineIdx,
    rawRange.startOffset + byteOffset,
    rawRange.endOffset + byteOffset,
  );
  const record: ReferenceRecord = {
    objectKind: listing.objectKind,
    objectName: listing.objectName,
    identifier,
    identifierKind,
    range: adjusted,
    referenceKind,
    parseStatus: 'ok',
    ...(hostTable ? { hostTable } : {}),
    ...(refPartCount !== undefined ? { refPartCount } : {}),
  };
  index.record(record);
}
